import React from "react";
import { Field, useFormikContext } from "formik";
import { Col, Form, Row } from "react-bootstrap";
import { AddressFormSection, AddressLookupProvider, AddressPanel } from "../../components/UI";
import { RequiredFieldMarker } from "../../components/Forms/RequiredFieldMarker";
import { useViewport } from "../../contexts";
import type { ISiteOption } from "../../customTypings/haulier/Haulier";
import { shouldShowFormFieldError } from "../../utils";

export interface HaulierEditFormGridProps {
  showSiteField: boolean;
  isNew: boolean;
  metaDeleted: boolean;
  siteId: string;
  setSiteId: (siteId: string) => void;
  siteDisplayName: string;
  sites: ISiteOption[];
  liveValidationAfterSubmit: boolean;
  errors: { name?: string; carrierLicenceNumber?: string };
  touched: { name?: boolean; carrierLicenceNumber?: boolean };
}

function GridFieldCell({ children }: { children: React.ReactNode }) {
  return <div className="mb-3">{children}</div>;
}

function HaulierSiteField({
  showSiteField,
  isNew,
  metaDeleted,
  siteId,
  setSiteId,
  siteDisplayName,
  sites,
}: Pick<
  HaulierEditFormGridProps,
  "showSiteField" | "isNew" | "metaDeleted" | "siteId" | "setSiteId" | "siteDisplayName" | "sites"
>) {
  if (!showSiteField) return null;

  return isNew ? (
    <Form.Group>
      <Form.Label>Site <RequiredFieldMarker /></Form.Label>
      <Form.Select
        value={siteId}
        onChange={(e) => setSiteId(e.target.value)}
        disabled={metaDeleted}
        aria-label="Site"
        required
      >
        <option value="">Select a site…</option>
        {sites.map((s) => (
          <option key={s.id} value={s.id}>
            {s.siteName}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  ) : (
    <Form.Group>
      <Form.Label>Site</Form.Label>
      <Form.Control readOnly disabled value={siteDisplayName} />
    </Form.Group>
  );
}

function HaulierNameField({
  metaDeleted,
  liveValidationAfterSubmit,
  errors,
  touched,
}: Pick<HaulierEditFormGridProps, "metaDeleted" | "liveValidationAfterSubmit" | "errors" | "touched">) {
  return (
    <Form.Group>
      <Form.Label>Name <RequiredFieldMarker /></Form.Label>
      <Field
        name="name"
        className={`form-control${shouldShowFormFieldError("name", errors, touched, liveValidationAfterSubmit) ? " is-invalid" : ""}`}
        disabled={metaDeleted}
      />
      {shouldShowFormFieldError("name", errors, touched, liveValidationAfterSubmit) && (
        <div className="invalid-feedback d-block">{errors.name}</div>
      )}
    </Form.Group>
  );
}

function HaulierCarrierLicenceField({
  metaDeleted,
  liveValidationAfterSubmit,
  errors,
  touched,
}: Pick<HaulierEditFormGridProps, "metaDeleted" | "liveValidationAfterSubmit" | "errors" | "touched">) {
  const { setFieldValue } = useFormikContext();

  return (
    <Form.Group>
      <Form.Label className="font-monospace">Carrier licence</Form.Label>
      <Field
        name="carrierLicenceNumber"
        className={`form-control font-monospace text-uppercase${shouldShowFormFieldError("carrierLicenceNumber", errors, touched, liveValidationAfterSubmit) ? " is-invalid" : ""}`}
        disabled={metaDeleted}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          void setFieldValue("carrierLicenceNumber", e.target.value.toUpperCase());
        }}
      />
      {shouldShowFormFieldError("carrierLicenceNumber", errors, touched, liveValidationAfterSubmit) && (
        <div className="invalid-feedback d-block">{errors.carrierLicenceNumber}</div>
      )}
    </Form.Group>
  );
}

function HaulierLicenceExpiryField({ metaDeleted }: Pick<HaulierEditFormGridProps, "metaDeleted">) {
  return (
    <Form.Group>
      <Form.Label>Licence expiry</Form.Label>
      <Field type="date" name="licenceExpiryDate" className="form-control" disabled={metaDeleted} />
    </Form.Group>
  );
}

function HaulierEnabledCheckbox() {
  return (
    <div className="form-check">
      <Field id="Haulier-is-enabled" name="isEnabled" type="checkbox" className="form-check-input" />
      <label className="form-check-label" htmlFor="Haulier-is-enabled">
        Enabled
      </label>
    </div>
  );
}

function HaulierAddressPanel({
  layoutVariant,
  postcodeLayout,
  formReadOnly,
}: {
  layoutVariant: "pairedRows" | "stacked";
  postcodeLayout: "below" | "separate";
  formReadOnly: boolean;
}) {
  return (
    <AddressPanel className="mb-3">
      <AddressFormSection
        layoutVariant={layoutVariant}
        postcodeLayout={postcodeLayout}
        requiredFields={[]}
        readOnly={formReadOnly}
      />
    </AddressPanel>
  );
}

/** Large: left Haulier fields, right address panel. */
export function HaulierEditFormGridLarge(props: HaulierEditFormGridProps) {
  return (
    <div className="vstack gap-2">
      <Row className="g-3">
        <Col lg={4}>
          <div className="vstack gap-2">
            {props.showSiteField && (
              <GridFieldCell>
                <HaulierSiteField {...props} />
              </GridFieldCell>
            )}
            <GridFieldCell>
              <HaulierNameField {...props} />
            </GridFieldCell>
            <GridFieldCell>
              <HaulierCarrierLicenceField {...props} />
            </GridFieldCell>
            <GridFieldCell>
              <HaulierLicenceExpiryField metaDeleted={props.metaDeleted} />
            </GridFieldCell>
            {props.isNew && (
              <GridFieldCell>
                <HaulierEnabledCheckbox />
              </GridFieldCell>
            )}
          </div>
        </Col>
        <Col lg={8}>
          <HaulierAddressPanel layoutVariant="pairedRows" postcodeLayout="below" formReadOnly={props.metaDeleted} />
        </Col>
      </Row>
    </div>
  );
}

/** Medium: left Haulier fields, right stacked address panel. */
export function HaulierEditFormGridMedium(props: HaulierEditFormGridProps) {
  return (
    <div className="vstack gap-2">
      <Row className="g-3">
        <Col md={6}>
          <div className="vstack gap-2">
            {props.showSiteField && (
              <GridFieldCell>
                <HaulierSiteField {...props} />
              </GridFieldCell>
            )}
            <GridFieldCell>
              <HaulierNameField {...props} />
            </GridFieldCell>
            <GridFieldCell>
              <HaulierCarrierLicenceField {...props} />
            </GridFieldCell>
            <GridFieldCell>
              <HaulierLicenceExpiryField metaDeleted={props.metaDeleted} />
            </GridFieldCell>
            {props.isNew && (
              <GridFieldCell>
                <HaulierEnabledCheckbox />
              </GridFieldCell>
            )}
          </div>
        </Col>
        <Col md={6}>
          <HaulierAddressPanel layoutVariant="stacked" postcodeLayout="below" formReadOnly={props.metaDeleted} />
        </Col>
      </Row>
    </div>
  );
}

/**
 * Narrow: Licence expiry, Name, Carrier licence, then address panel, then Enabled (new only).
 */
export function HaulierEditFormGridNarrow(props: HaulierEditFormGridProps) {
  return (
    <>
      {props.showSiteField && (
        <div className="mb-3">
          <HaulierSiteField {...props} />
        </div>
      )}
      <div className="mb-3">
        <HaulierLicenceExpiryField metaDeleted={props.metaDeleted} />
      </div>
      <div className="mb-3">
        <HaulierNameField {...props} />
      </div>
      <div className="mb-3">
        <HaulierCarrierLicenceField {...props} />
      </div>
      <HaulierAddressPanel layoutVariant="stacked" postcodeLayout="separate" formReadOnly={props.metaDeleted} />
      {props.isNew && (
        <div className="mb-3">
          <HaulierEnabledCheckbox />
        </div>
      )}
    </>
  );
}

export function HaulierEditFormGrid(props: HaulierEditFormGridProps) {
  const { width } = useViewport();

  return (
    <AddressLookupProvider>
      {width >= 992 ? (
        <HaulierEditFormGridLarge {...props} />
      ) : width >= 768 ? (
        <HaulierEditFormGridMedium {...props} />
      ) : (
        <HaulierEditFormGridNarrow {...props} />
      )}
    </AddressLookupProvider>
  );
}
